import React, { useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setSelectedFilters } from "../../src/store/globalFilterSlice";
import PropTypes from "prop-types";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// Demo token (replace with your own for production)
mapboxgl.accessToken = "pk.eyJ1IjoiaWNvZ3oiLCJhIjoiY2w1MHM4bTVpM20ycDNicXp3Y2Y2cWNneiJ9._TK3kEhvF6n1KUc1079_vg";

const MapComponent = ({
    geoJson,
    mapLevel,
    northPoint,
    southPoint,
    centerPoint,
    zoomLevel,
    onChange,
    toolTipValues,
    toolTipLabels,
    growthDegrowth,
    hoverData,
    filterType = 'global', // <-- add default prop
}) => {
    const mapContainerRef = useRef(null);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!geoJson) return;
        // Parse center point
        const [lng, lat] = centerPoint[0].split(",").map(Number);
        // Create map
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: "mapbox://styles/icogz/cm8zrgh7y005701s6afu3dey9",
            center: [lng, lat],
            zoom: zoomLevel,
        });
        // Add markers
        geoJson.features.forEach((feature, idx) => {
            // Ensure coordinates are numbers and [lng, lat] order
            let markerLng, markerLat;
            if (Array.isArray(feature.geometry.coordinates)) {
                markerLng = Number(feature.geometry.coordinates[0]);
                markerLat = Number(feature.geometry.coordinates[1]);
            } else if (typeof feature.geometry.coordinates === 'string') {
                // If coordinates are a string like "55.2708,25.2048"
                const coords = feature.geometry.coordinates.split(',').map(Number);
                markerLng = coords[0];
                markerLat = coords[1];
            }
            // Debug log for marker position
            console.log('Marker', idx, 'Lng:', markerLng, 'Lat:', markerLat, feature.properties);
            // Use color from hoverData if available, else fallback
            const countryId = feature.properties.country_id;
            const hover = hoverData && countryId ? hoverData[countryId] : null;
            let color = "#06a57d"; // default green
            if (hover && hover.color) {
                color = hover.color;
            } else if (growthDegrowth && feature.properties[growthDegrowth] < 0) {
                color = "#b51c2a"; // red for degrowth
            }
            // Create marker
            const marker = new mapboxgl.Marker({ color })
                .setLngLat([markerLng, markerLat])
                .addTo(map);
            const markerEl = marker.getElement();
            markerEl.style.zIndex = 1000;
            markerEl.style.cursor = "pointer";
            markerEl.style.pointerEvents = "auto";

            // Tooltip logic for country marker
            const tooltipKeys = ["market_share", "sales_value", "sales_value_growth"];
            const tooltipLabels = ["Market Share%", "Sales Value", "Sales Value Growth%"];
            // Format values
            function formatValue(key, value) {
                if (value === undefined || value === null || value === "") return "-";
                if (key === "market_share" || key === "sales_value_growth") {
                    return Number(value).toFixed(2) + "%";
                }
                if (key === "sales_value") {
                    let num = Number(value);
                    if (num >= 1000000) return (num / 1000000).toFixed(2) + "M";
                    if (num >= 1000) return (num / 1000).toFixed(2) + "K";
                    return num.toFixed(2);
                }
                return value;
            }
            // Build styled tooltip HTML
            let tooltipHtml = `<div style='font-size:12px;min-width:180px;color:#fff;font-family:Ubuntu,sans-serif;font-weight:700;'>`;
            tooltipHtml += `<div style='font-size:14px;font-family:Ubuntu,sans-serif;font-weight:700;color:#fff;margin-bottom:4px;'>${feature.properties.country_name || feature.properties.name || "Region"}</div>`;
            for (let i = 0; i < tooltipKeys.length; i++) {
                let value = "-";
                if (hover && hover[tooltipKeys[i]] !== undefined && hover[tooltipKeys[i]] !== null) {
                    value = formatValue(tooltipKeys[i], hover[tooltipKeys[i]]);
                } else if (feature.properties[tooltipKeys[i]] !== undefined && feature.properties[tooltipKeys[i]] !== null) {
                    value = formatValue(tooltipKeys[i], feature.properties[tooltipKeys[i]]);
                }
                tooltipHtml += `<div style='font-family:Ubuntu,sans-serif;font-size:12px;font-weight:700;color:#fff;'><span style='font-weight:700;'>${tooltipLabels[i]}:</span> <span style='font-weight:700;'>${value}</span></div>`;
            }
            tooltipHtml += `</div>`;
            // Popup (attach to marker for automatic hover)
            const popup = new mapboxgl.Popup({ offset: 12, closeButton: false, closeOnClick: false }).setHTML(tooltipHtml);
            marker.setPopup(popup);
            // Fallback: ensure popup appears on hover
            markerEl.addEventListener("mouseenter", () => popup.addTo(map));
            markerEl.addEventListener("mouseleave", () => popup.remove());
            markerEl.addEventListener("click", () => {
                // Update global/cross filter with selected country (correct payload)
                const countryId = feature.properties.country_id;
                if (countryId) {
                    dispatch(setSelectedFilters({ filters: { country: [countryId] }, filterType }));
                }
                if (onChange) onChange(feature.properties);
            });
        });
        // Fit bounds if needed
        // if (geoJson.features.length > 1) {
        //     const bounds = new mapboxgl.LngLatBounds();
        //     geoJson.features.forEach(f => bounds.extend(f.geometry.coordinates));
        //     map.fitBounds(bounds, { padding: 40 });
        // }
        map.addControl(new mapboxgl.NavigationControl(), "top-right");

        // Clean up on unmount
        return () => map.remove();
    }, [geoJson, centerPoint, zoomLevel, toolTipValues, toolTipLabels, growthDegrowth, onChange, hoverData, filterType]);
    return (
        <div
            ref={mapContainerRef}
            style={{ width: "100%", height: "350px", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px #0001" }}
        />
    );
};

MapComponent.propTypes = {
    geoJson: PropTypes.object.isRequired,
    mapLevel: PropTypes.any,
    northPoint: PropTypes.any,
    southPoint: PropTypes.any,
    centerPoint: PropTypes.any,
    zoomLevel: PropTypes.number,
    onChange: PropTypes.func,
    toolTipValues: PropTypes.array,
    toolTipLabels: PropTypes.array,
    growthDegrowth: PropTypes.string,
    hoverData: PropTypes.object,
    filterType: PropTypes.string, // <-- add propType
};

export default MapComponent; 